import React, { useMemo } from "react";
import { LoadingIndicator } from "@jsq/ds/core";
import { ErrorPage, ErrorType } from "../../../../common/components/ErrorPage";
import { useGetDedupedPaymentPrefsQuery } from "../../../../graphql/supergraph.index";
import { WorkflowMode } from "../../common/enums";
import { AccountsPaymentPrefConfirmation } from "../../Components/AccountsPaymentPrefConfirmation";
import { SelectAvailablePaymentPrefsFormWithoutPositions } from "../../Components/SelectAvailablePaymentPrefsForm";
import { SummaryOfChanges } from "../../Components/SummaryOfChanges";
import { ChildComponentProps } from "../../Components/types";
import { ConsolidatedPaymentPrefProvider } from "../../ConsolidatedPaymentPrefContextProvider";
import { BasePaymentPrefWorkflow } from "../BaseWorkflow";
import { type PaymentPrefCreationForAccountsWorkflowProps } from "./types";
import { createLpHeaderComponent, getAvailablePaymentMethods } from "./utils";

/**
 * PaymentPrefCreationForAccountsWorkflow component.
 *
 * This component handles the workflow for creating a payment preference for portal 2.0 / LP accounts.
 * Today - this is only used for LPs but may be extended to support the ability to bulk create payment
 * preferences for many accounts potentialy across different arenas in canopy.
 *
 * It fetches existing payment preferences that the user has access to,
 * and provides the appropriate forms and components for the user to complete the workflow.
 *
 * @component
 * @param {Object} props - The component props.
 * @param {Function} props.onExit - Function to call when the workflow is exited.
 * @param {Function} props.onSuccess - Function to call when the workflow is successfully completed.
 * @param {Array} props.accounts - An array of account edges.
 *
 * @returns {JSX.Element} The rendered component.
 *
 * @example
 * <PaymentPrefCreationForAccountsWorkflow
 *   onExit={handleExit}
 *   onSuccess={handleSuccess}
 *   accounts={[{ __typename: 'AccountEdge', node: { id: '123', __typename: 'AccountObjectType' } }]}
 * />
 */
export const PaymentPrefCreationForAccountsWorkflow: React.FC<
  PaymentPrefCreationForAccountsWorkflowProps
> = ({ onExit, onSuccess, accounts }) => {
  // Load existing payment prefs that the user has access to
  const {
    data: existingPaymentPrefsData,
    loading: existingPaymentPrefsLoading,
    error: existingPaymentPrefsError,
  } = useGetDedupedPaymentPrefsQuery({
    fetchPolicy: "cache-and-network",
  });
  const existingPaymentPrefs = existingPaymentPrefsData?.dedupedPaymentPrefs;
  const availablePaymentMethods = useMemo(
    () => getAvailablePaymentMethods(accounts),
    [accounts],
  );

  // If there is an existing payment pref then we must allow them to be selected
  let prefixComponents: React.FC<ChildComponentProps>[] = [];
  if (existingPaymentPrefs && existingPaymentPrefs.length > 0) {
    prefixComponents = [SelectAvailablePaymentPrefsFormWithoutPositions];
  }

  if (existingPaymentPrefsLoading) {
    return <LoadingIndicator />;
  }

  if (existingPaymentPrefsError) {
    return <ErrorPage error={ErrorType.InternalError} />;
  }

  return (
    <ConsolidatedPaymentPrefProvider
      availablePaymentMethods={availablePaymentMethods}
      accounts={accounts}
      existingPaymentPrefs={existingPaymentPrefs}
      workflowMode={WorkflowMode.Create}
      initialSelectAvailablePaymentPrefsFormHeaderComponent={createLpHeaderComponent(accounts)}
    >
      <BasePaymentPrefWorkflow
        workflowMode={WorkflowMode.Create}
        prefixComponents={prefixComponents}
        suffixComponents={[AccountsPaymentPrefConfirmation, SummaryOfChanges]}
        onExit={onExit}
        onSuccess={onSuccess}
      />
    </ConsolidatedPaymentPrefProvider>
  );
};

export type { PaymentPrefCreationForAccountsWorkflowProps };
